import React, { useEffect, useRef, useState } from "react";
import { handlePostFeedback } from "../../utils/api";
import { RATING_TAG_CODE, errorCode, timer, track } from "../../analytics";
import styles from "./style.module.css";

// Rating card from "Tracking Result.dc.html", shown for delivered shipments.
// Saved through POST /api/track/rating → logistic /api/rating (table rating_logs):
//   shipperRating = stars (1–5), userRating = recommend (1–5; logistic rejects > 5),
//   shipperFeedback = selected tags + comment.
// The design's 0–10 recommend scale is shown as 1–5 to match the stored range.

const STAR_LABELS = ["Tap a star to rate", "Terrible", "Bad", "Okay", "Good", "Excellent"];
const GOOD_TAGS = ["On time", "Polite Kaptaan", "Safe packaging", "Followed instructions", "Called before arriving"];
const BAD_TAGS = ["Late delivery", "Damaged package", "Rude behaviour", "Did not call", "Wrong location"];
const RECOMMEND = [1, 2, 3, 4, 5];

const cx = (...c) => c.filter(Boolean).join(" ");
const firstName = (name) => {
  const w = String(name || "").trim().split(/\s+/)[0];
  return w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : "";
};

const Star = ({ on }) => (
  <svg className={styles.starSvg} viewBox="0 0 24 24" fill={on ? "#7130F0" : "#fff"} stroke={on ? "#7130F0" : "#C9D2E6"} strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2.8l2.8 5.7 6.3.9-4.55 4.45 1.07 6.27L12 17.2l-5.62 2.92 1.07-6.27L2.9 9.4l6.3-.9z" />
  </svg>
);

const RatingCard = ({ awb, ratingLog, riderName, brand }) => {
  const savedStar = Number(ratingLog?.shipperRating) || 0;
  const [submitted, setSubmitted] = useState(savedStar > 0 ? savedStar : 0);
  const [star, setStar] = useState(0);
  const [hover, setHover] = useState(0);
  const [tags, setTags] = useState([]);
  const [recommend, setRecommend] = useState(null);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  // Funnel: tp_rating steps from the card appearing to the feedback being saved.
  const opened = useRef(timer());
  const step = (name, extra) => track("tp_rating", { step: name, ...extra });
  useEffect(() => {
    if (!savedStar) step("shown");
    // Once, when the card appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rider = firstName(riderName);
  const shown = hover || star;
  const good = star >= 4;
  const brandName = brand || "the brand";

  const pickStar = (n) => {
    // Moving between "good" (4–5) and "bad" (1–3) changes the tag list.
    if ((star >= 4) !== (n >= 4)) setTags([]);
    step("star", { stars: n });
    setStar(n);
    setHover(0);
  };
  const toggleTag = (label) => {
    if (!tags.includes(label)) step("tags", { tag: RATING_TAG_CODE[label] || label });
    setTags((t) => (t.includes(label) ? t.filter((x) => x !== label) : [...t, label]));
  };

  const submit = async () => {
    if (!star || sending) return;
    setSending(true);
    setError("");
    const shipperFeedback = [tags.join(", "), note.trim()].filter(Boolean).join(" — ");
    const res = await handlePostFeedback({ awb, shipperRating: star, userRating: recommend ?? 0, shipperFeedback });
    setSending(false);
    const result = { stars: star, recommend: recommend ?? 0, tags: tags.map((t) => RATING_TAG_CODE[t] || t), has_comment: !!note.trim(), elapsed_ms: opened.current() };
    if (res?.status === "Success") {
      step("submitted", result);
      setSubmitted(star);
    } else {
      step("failed", { ...result, code: errorCode(res) });
      setError("We couldn't send your feedback. Please try again.");
    }
  };

  if (submitted) {
    return (
      <div className={styles.card}>
        <div className={styles.thanks}>
          <span className={styles.thanksIcon}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path className={styles.draw} d="M5 12.5l4.5 4.5L19 7.5" strokeDasharray="60" />
            </svg>
          </span>
          <span className={styles.thanksTitle}>Thanks for your feedback</span>
          <span className={styles.thanksBody}>
            You rated {rider || "your delivery"} {submitted}/5. Your feedback helps Blitz{brand ? ` and ${brand}` : ""} make every delivery better.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.grid}>
        <div className={styles.pane}>
          <div className={styles.head}>
            <span className={styles.eyebrow}>Rate your delivery</span>
            <span className={styles.question}>{rider ? `How was your delivery with ${rider}?` : "How was your delivery?"}</span>
          </div>
          <div className={styles.starBlock}>
            <div className={styles.stars} onMouseLeave={() => setHover(0)} role="radiogroup" aria-label="Rate your delivery">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={star === n}
                  aria-label={`${n} star${n > 1 ? "s" : ""}, ${STAR_LABELS[n]}`}
                  className={styles.starBtn}
                  style={{ transform: `scale(${hover && n <= hover ? 1.12 : 1})` }}
                  onMouseEnter={() => setHover(n)}
                  onFocus={() => setHover(n)}
                  onBlur={() => setHover(0)}
                  onClick={() => pickStar(n)}
                >
                  <Star on={n <= shown} />
                </button>
              ))}
            </div>
            <span className={cx(styles.starLabel, shown && styles.starLabelOn)}>{STAR_LABELS[shown]}</span>
          </div>
          {star > 0 && (
            <div className={styles.tagBlock}>
              <span className={styles.tagQ}>{good ? "What went well?" : "What could have been better?"}</span>
              <div className={styles.chips}>
                {(good ? GOOD_TAGS : BAD_TAGS).map((label) => {
                  const on = tags.includes(label);
                  return (
                    <button key={label} type="button" aria-pressed={on} className={cx(styles.chip, on && styles.chipOn)} onClick={() => toggleTag(label)}>
                      {on && (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M5 12.5l4.5 4.5L19 7.5" />
                        </svg>
                      )}
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className={cx(styles.pane, styles.paneAlt)}>
          <div className={styles.head}>
            <span className={styles.eyebrow}>Recommend {brandName}</span>
            <span className={styles.questionSm}>How likely are you to recommend {brandName} to a friend?</span>
          </div>
          <div className={styles.npsBlock}>
            <div className={styles.nps} role="radiogroup" aria-label={`How likely are you to recommend ${brandName}`}>
              {RECOMMEND.map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={recommend === n}
                  aria-label={`${n} out of 5`}
                  className={cx(styles.npsBtn, recommend === n && styles.npsOn)}
                  onClick={() => {
                    step("recommend", { recommend: n });
                    setRecommend(n);
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className={styles.npsLabels}>
              <span>Not at all likely</span>
              <span>Extremely likely</span>
            </div>
          </div>
          {showNote && (
            <textarea
              className={styles.note}
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Tell us more (optional)"
              maxLength={500}
              autoFocus
            />
          )}
          {error && <span className={styles.error}>{error}</span>}
          <div className={styles.actions}>
            {!showNote && (
              <button type="button" className={styles.addNote} onClick={() => setShowNote(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add a comment
              </button>
            )}
            <button type="button" className={cx(styles.submit, star > 0 && styles.submitOn)} onClick={submit} disabled={!star || sending}>
              {sending ? "Sending…" : "Submit feedback"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RatingCard;
